import { useState, useRef, useEffect, useCallback } from "react";
import { useCalculatorStore } from "../store/calculatorStore";
import { useSharingStore } from "../store/sharingStore";
import {
  saveTextToLocalFile,
  openTextFromLocalFile,
} from "../store/workspaceStore";
import { track } from "../lib/track";

const SESSION_EXT = ".quanta";

// Turn a session name into a safe file stem for `<stem>.quanta.json`.
function toFileStem(name: string): string {
  const stem = name.trim().replace(/[^\w.-]+/g, "_").replace(/^_+|_+$/g, "");
  return stem || "session";
}

export function SessionTabs() {
  const {
    sessions,
    activeSessionId,
    createSession,
    deleteSession,
    renameSession,
    duplicateSession,
    switchSession,
    archiveSession,
    updateSessionColor,
    reorderSessions,
  } = useCalculatorStore();

  const { shareConfigs } = useSharingStore();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [contextMenu, setContextMenu] = useState<{
    sessionId: string;
    x: number;
    y: number;
  } | null>(null);
  // Right-click on an empty part of the tab bar.
  const [barMenu, setBarMenu] = useState<{ x: number; y: number } | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dragSourceId, setDragSourceId] = useState<string | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollLeft, setShowScrollLeft] = useState(false);
  const [showScrollRight, setShowScrollRight] = useState(false);

  // Filter to show only non-archived sessions
  const visibleSessions = sessions
    .filter((s) => !s.isArchived)
    .sort((a, b) => a.position - b.position);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  // Close context menus on outside click
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null);
      setBarMenu(null);
    };
    if (contextMenu || barMenu) {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [contextMenu, barMenu]);

  // Listen for F2 rename event from keyboard shortcuts
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.sessionId) {
        const session = sessions.find((s) => s.id === detail.sessionId);
        if (session) {
          setEditingId(session.id);
          setEditingName(session.name);
        }
      }
    };
    window.addEventListener("quanta-rename-session", handler);
    return () => window.removeEventListener("quanta-rename-session", handler);
  }, [sessions]);

  // Tab overflow detection
  const checkOverflow = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    setShowScrollLeft(container.scrollLeft > 0);
    setShowScrollRight(
      container.scrollLeft + container.clientWidth < container.scrollWidth - 1,
    );
  }, []);

  useEffect(() => {
    checkOverflow();
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", checkOverflow);
      const ro = new ResizeObserver(checkOverflow);
      ro.observe(container);
      return () => {
        container.removeEventListener("scroll", checkOverflow);
        ro.disconnect();
      };
    }
  }, [checkOverflow, visibleSessions.length]);

  const scrollTabs = (direction: "left" | "right") => {
    const container = containerRef.current;
    if (!container) return;
    const scrollAmount = 150;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  // --- Drag-and-drop handlers ---
  const handleDragStart = (e: React.DragEvent, sessionId: string) => {
    setDragSourceId(sessionId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", sessionId);
    // Slight delay for drag ghost
    requestAnimationFrame(() => {
      setDragSourceId(sessionId);
    });
  };

  const handleDragOver = (e: React.DragEvent, sessionId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (sessionId !== dragSourceId) {
      setDragOverId(sessionId);
    }
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDragOverId(null);
    setDragSourceId(null);

    const sourceId = e.dataTransfer.getData("text/plain");
    if (!sourceId || sourceId === targetId) return;

    const currentOrder = visibleSessions.map((s) => s.id);
    const sourceIdx = currentOrder.indexOf(sourceId);
    const targetIdx = currentOrder.indexOf(targetId);

    if (sourceIdx === -1 || targetIdx === -1) return;

    // Remove source and insert at target position
    currentOrder.splice(sourceIdx, 1);
    currentOrder.splice(targetIdx, 0, sourceId);

    // Include archived sessions at the end (preserve their order)
    const archivedIds = sessions
      .filter((s) => s.isArchived)
      .sort((a, b) => a.position - b.position)
      .map((s) => s.id);

    reorderSessions([...currentOrder, ...archivedIds]);
  };

  const handleDragEnd = () => {
    setDragSourceId(null);
    setDragOverId(null);
  };

  const handleTabClick = (id: string) => {
    if (editingId !== id) {
      switchSession(id);
    }
  };

  const handleDoubleClick = (session: (typeof sessions)[0]) => {
    setEditingId(session.id);
    setEditingName(session.name);
  };

  const handleRenameSubmit = () => {
    if (editingId && editingName.trim()) {
      renameSession(editingId, editingName.trim());
    }
    setEditingId(null);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleRenameSubmit();
    } else if (e.key === "Escape") {
      setEditingId(null);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, sessionId: string) => {
    e.preventDefault();
    e.stopPropagation(); // don't also open the tab-bar menu
    setBarMenu(null);
    setContextMenu({
      sessionId,
      x: e.clientX,
      y: e.clientY,
    });
  };

  // Right-click on empty tab-bar space.
  const handleBarContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu(null);
    setBarMenu({ x: e.clientX, y: e.clientY });
  };

  // Save a session to the local file system (native "Save As", or a download).
  const handleSaveSession = async (sessionId: string) => {
    const store = useCalculatorStore.getState();
    const session = store.sessions.find((s) => s.id === sessionId);
    const data = store.exportSessionData(sessionId);
    if (!session || !data) return;
    await saveTextToLocalFile(
      `${toFileStem(session.name)}${SESSION_EXT}`,
      JSON.stringify(data, null, 2),
      { "application/json": [SESSION_EXT] },
    );
    track("session-saved");
  };

  // Open a saved session file from the local file system as a new session.
  // `.quanta` is a single extension the File System Access API can filter on;
  // the picker still offers "All Files" for older `.quanta.json` files, and the
  // contents are validated on import regardless.
  const handleOpenSessionFromFile = async () => {
    const picked = await openTextFromLocalFile({
      "application/json": [".quanta"],
    });
    if (!picked) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(picked.content);
    } catch {
      window.alert(`${picked.name} is not a valid JSON file.`);
      return;
    }
    const newId = useCalculatorStore.getState().importSessionData(parsed);
    if (!newId) {
      window.alert(`${picked.name} is not a Quanta session file.`);
      return;
    }
    track("session-opened");
  };

  const handleDeleteTab = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const session = sessions.find((s) => s.id === id);
    const sessionHistory = useCalculatorStore
      .getState()
      .history.filter((h) => h.sessionId === id);

    if (sessionHistory.length > 0) {
      const confirmed = window.confirm(
        `Delete "${session?.name}"? This will permanently delete ${sessionHistory.length} calculation${sessionHistory.length === 1 ? "" : "s"}.`,
      );
      if (!confirmed) return;
    }

    deleteSession(id);
  };

  const handleNewSession = () => {
    const newId = createSession();
    // Start editing the new session name
    setTimeout(() => {
      const newSession = useCalculatorStore
        .getState()
        .sessions.find((s) => s.id === newId);
      if (newSession) {
        setEditingId(newId);
        setEditingName(newSession.name);
      }
    }, 0);
  };

  const SESSION_COLORS = [
    { name: "Indigo", value: "#6366f1" },
    { name: "Cyan", value: "#22d3ee" },
    { name: "Amber", value: "#f59e0b" },
    { name: "Pink", value: "#ec4899" },
    { name: "Emerald", value: "#10b981" },
    { name: "Violet", value: "#8b5cf6" },
    { name: "Red", value: "#ef4444" },
    { name: "Blue", value: "#3b82f6" },
  ];

  return (
    <div className="session-tabs">
      {showScrollLeft && (
        <button
          className="session-tabs-scroll session-tabs-scroll-left"
          onClick={() => scrollTabs("left")}
          aria-label="Scroll tabs left"
        >
          ‹
        </button>
      )}
      <div
        className="session-tabs-container"
        ref={containerRef}
        onContextMenu={handleBarContextMenu}
      >
        {visibleSessions.map((session) => {
          const shareMode = shareConfigs[session.id]?.shareMode || "private";
          const shareClass =
            shareMode === "account"
              ? "shared-account"
              : shareMode === "public"
                ? "shared-public"
                : "";
          const isDragOver = dragOverId === session.id;
          const isDragSource = dragSourceId === session.id;
          return (
            <div
              key={session.id}
              className={`session-tab ${activeSessionId === session.id ? "active" : ""} ${shareClass} ${isDragOver ? "drag-over" : ""} ${isDragSource ? "drag-source" : ""}`}
              onClick={() => handleTabClick(session.id)}
              onDoubleClick={() => handleDoubleClick(session)}
              onMouseDown={(e) => {
                if (e.button === 1 && visibleSessions.length > 1) {
                  e.preventDefault();
                  handleDeleteTab(session.id, e);
                }
              }}
              onContextMenu={(e) => handleContextMenu(e, session.id)}
              draggable={editingId !== session.id}
              onDragStart={(e) => handleDragStart(e, session.id)}
              onDragOver={(e) => handleDragOver(e, session.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, session.id)}
              onDragEnd={handleDragEnd}
              style={
                { "--session-color": session.color } as React.CSSProperties
              }
            >
              <span className="session-tab-indicator" />
              {editingId === session.id ? (
                <input
                  ref={editInputRef}
                  type="text"
                  className="session-tab-input"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={handleRenameSubmit}
                  onKeyDown={handleRenameKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  maxLength={50}
                />
              ) : (
                <span className="session-tab-name">{session.name}</span>
              )}
              {visibleSessions.length > 1 && (
                <button
                  className="session-tab-close"
                  onClick={(e) => handleDeleteTab(session.id, e)}
                  title="Close session"
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
        <button
          className="session-tab-new"
          onClick={handleNewSession}
          title="New session (Ctrl+T)"
        >
          +
        </button>
      </div>
      {showScrollRight && (
        <button
          className="session-tabs-scroll session-tabs-scroll-right"
          onClick={() => scrollTabs("right")}
          aria-label="Scroll tabs right"
        >
          ›
        </button>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="session-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={() => {
              const session = sessions.find(
                (s) => s.id === contextMenu.sessionId,
              );
              if (session) {
                setEditingId(session.id);
                setEditingName(session.name);
              }
              setContextMenu(null);
            }}
          >
            Rename
          </button>
          <button
            onClick={() => {
              duplicateSession(contextMenu.sessionId);
              setContextMenu(null);
            }}
          >
            Duplicate
          </button>
          <button
            onClick={() => {
              const id = contextMenu.sessionId;
              setContextMenu(null);
              void handleSaveSession(id);
            }}
          >
            Save to file…
          </button>
          <div className="context-menu-divider" />
          <div className="context-menu-colors">
            <span className="context-menu-label">Color</span>
            <div className="color-swatches">
              {SESSION_COLORS.map((color) => (
                <button
                  key={color.value}
                  className="color-swatch"
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                  onClick={() => {
                    updateSessionColor(contextMenu.sessionId, color.value);
                    setContextMenu(null);
                  }}
                />
              ))}
            </div>
          </div>
          <div className="context-menu-divider" />
          <button
            onClick={() => {
              archiveSession(contextMenu.sessionId);
              setContextMenu(null);
            }}
          >
            Archive
          </button>
          {visibleSessions.length > 1 && (
            <button
              className="danger"
              onClick={() => {
                handleDeleteTab(contextMenu.sessionId);
                setContextMenu(null);
              }}
            >
              Delete
            </button>
          )}
        </div>
      )}

      {/* Tab-bar Context Menu (right-click empty space) */}
      {barMenu && (
        <div
          className="session-context-menu"
          style={{ left: barMenu.x, top: barMenu.y }}
        >
          <button
            onClick={() => {
              setBarMenu(null);
              handleNewSession();
            }}
          >
            New session
          </button>
          <button
            onClick={() => {
              setBarMenu(null);
              void handleOpenSessionFromFile();
            }}
          >
            Open session from file…
          </button>
        </div>
      )}
    </div>
  );
}
